"use client";
import * as React from "react";
import Image from "next/image";
import { FormInput } from "./FormInput";
import { PhoneInput } from "./PhoneInput";
import { SecurityInfo } from "./SecurityInfo";
import { Icons } from "@/app/assets/shared";
import dynamic from "next/dynamic";
import Script from "next/script";
const ReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
  ssr: false,
});
import { FormProps, FormData } from "@/lib/Types";
import clsx from "clsx";
import { FileUpload } from "./FileUpload";
import { useRouter, usePathname } from "next/navigation";

import {
  getAWSsignUrl,
  uploadFileToS3,
  saveEnquiryForm,
  getCountryList,
} from "@/lib/ApiClient";

import { normalizeFileType } from "@/lib/Utility";
import CustomSelect from "./CustomSelect";

export const initialData: FormData = {
  fullName: "",
  email: "",
  phone: "",
  countryCode: "",
  country: "",
  interestedIn: "",
  yourBudget: "",
  skypeWhatsapp: "",
  message: "",
  sendMeNda: false,
  file: "",
  gRecaptchaResponse: "",
};

// ─────────────────────────────────────────────────────────────────────────────

const INTERESTED_IN_OPTIONS = [
  { value: "Artificial Intelligence", label: "Artificial Intelligence" },
  { value: "AR / VR", label: "AR / VR" },
  {
    value: "Enterprise Software Solutions",
    label: "Enterprise Software Solutions",
  },
  { value: "Mobile App Development", label: "Mobile App Development" },
  { value: "Web Development", label: "Web Development" },
  { value: "Hire Dedicated Team", label: "Hire Dedicated Team" },
  { value: "IT Consulting Services", label: "IT Consulting Services" },
  { value: "Partnership Opportunities", label: "Partnership Opportunities" },
];

const BUDGET_OPTIONS = [
  { value: "Less than $5,000", label: "Less than $5,000" },
  { value: "$5,000 - $10,000", label: "$5,000 - $10,000" },
  { value: "$10,000 - $20,000", label: "$10,000 - $20,000" },
  { value: "$20,000 - $35,000", label: "$20,000 - $35,000" },
  { value: "$35,000 - $50,000", label: "$35,000 - $50,000" },
  { value: "$50,000 - $1,00,000", label: "$50,000 - $1,00,000" },
  { value: "$1,00,000+", label: "$1,00,000+" },
];

const Form: React.FC<FormProps> = ({ variant = "light" }) => {
  const baseHeightClass = "h-[var(--primarybtn-height)]";
  const textSizeClass = "text-[length:var(--primarybtn)] min-[2000px]:text-[19px]";
  const paddingClass = "lg:px-10 px-5";
  const iconWrapperClass = "w-[var(--primarybtn-height)] h-[var(--primarybtn-height)] ml-0";
  const iconSize = 16;
  const pathname = usePathname();
  const router = useRouter();

  let lastSegment = "/";
  if (pathname && pathname !== "/") {
    const segments = pathname.split("/").filter(Boolean);
    lastSegment = segments[segments.length - 1] || "/";
  }

  const [selectedCountry, setSelectedCountry] = React.useState({
    slug: "US",
    code: "+1",
  });

  const [countryListArray, setCountryListArray] = React.useState<
    { slug: string; code: string; countryName: string }[]
  >([]);

  const countryList = React.useMemo(() => {
    return countryListArray.map((c) => ({
      label: c.countryName,
      value: c.countryName,
    }));
  }, [countryListArray]);

  const [isChecked, setIsChecked] = React.useState(false);
  const [recaptchaToken, setRecaptchaToken] = React.useState<string>("");
  const [loadRecaptcha, setLoadRecaptcha] = React.useState(false);
  const [formData, setFormData] = React.useState<FormData>(initialData);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const [isMessageFocused, setIsMessageFocused] = React.useState(false);

  const handleInputChange = React.useCallback(
    (field: string) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
        const value = e?.target?.value ?? e;
        setFormData((prev) => ({ ...prev, [field]: value }));
      },
    [],
  );

  //Change Country Code And Country According to Each Other
  const handleChangeCountryAccCode = React.useCallback(
    (country: string) => {
      const selectedCountryData = countryListArray.find(
        (c: any) => c.countryName === country,
      );

      if (!selectedCountryData) return;

      setSelectedCountry({
        slug: selectedCountryData.slug,
        code: selectedCountryData.code,
      });

      setFormData((prev: any) => ({
        ...prev,
        country,
        countryCode: selectedCountryData.code,
      }));
    },
    [countryListArray],
  );

  //Validate Form Data
  const validate = React.useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName?.trim())
      newErrors.fullName = "Full Name is required";

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.countryCode?.trim())
      newErrors.countryCode = "Country code is required";

    if (!formData.country?.trim()) newErrors.country = "Country is required";

    if (!formData.phone?.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{6,15}$/.test(formData.phone.replace(/\s|\-|\+/g, ""))) {
      newErrors.phone = "Invalid phone number";
    }

    if (!formData.interestedIn?.trim())
      newErrors.interestedIn = "This field is required";

    if (!formData.yourBudget?.trim())
      newErrors.yourBudget = "Budget is required";

    if (!formData.message?.trim()) newErrors.message = "Message is required";

    if (recaptchaSiteKey) {
      const token = recaptchaToken;
      if (!token) {
        newErrors.gRecaptchaResponse = "Please complete the captcha.";
      }
    }

    return newErrors;
  }, [formData, recaptchaToken, recaptchaSiteKey]);

  //Save Form Data
  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (submitting) return;
      setSubmitError(null);

      const validationErrors = await validate();
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length !== 0) {
        return;
      }

      setSubmitting(true);
      try {
        let filePreview = "";
        const MAX_FILE_SIZE_MB = 10;
        const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

        if (formData?.file && (formData.file as any).size) {
          const fileObj = formData.file as any;

          if (fileObj.size > MAX_FILE_SIZE_BYTES) {
            throw new Error(
              `File size exceeds limit. Max allowed is ${MAX_FILE_SIZE_MB}MB`,
            );
          }

          const fileType = normalizeFileType(
            fileObj.type || fileObj.name || "",
          );
          if (!fileType) {
            throw new Error("Unsupported file format.");
          }

          const signedUrlResp: any = await getAWSsignUrl(fileType);

          if (signedUrlResp?.url) {
            await uploadFileToS3(signedUrlResp.url, fileObj);
            filePreview = signedUrlResp.preview || signedUrlResp.url || "";
          }
        }

        const token = recaptchaToken || "";

        const payload: any = {
          ...formData,
          file: filePreview,
          gRecaptchaResponse: token,
        };
        const saveDataResp = await saveEnquiryForm(payload);
        if (saveDataResp?.success) {
          setFormData(initialData);
          try {
            (window as any).grecaptcha?.reset?.();
          } catch (e) {
            console.log(e);
          }
          router.push(
            `/thank-you?type=${lastSegment === "/" ? "home" : lastSegment}`,
          );
        } else {
          throw new Error(saveDataResp?.message || "Failed to save enquiry");
        }
      } catch (err: any) {
        console.error(err);
        setSubmitError(
          err?.message || "Something went wrong while submitting the form.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [formData, recaptchaToken, submitting, validate, router, lastSegment],
  );

  const ensureLoadRecaptcha = React.useCallback(
    () => setLoadRecaptcha(true),
    [],
  );

  //Fetch Country List
  const fetchCountryList = React.useCallback(async (): Promise<void> => {
    try {
      const data: any = await getCountryList();
      const items = data?.items ?? [];
      setCountryListArray(items);
    } catch (err) {
      console.error(
        err instanceof Error ? err.message : "Failed to fetch country list",
      );
    }
  }, []);

  React.useEffect(() => {
    fetchCountryList();
  }, [fetchCountryList]);

  return (
    <form
      onSubmit={handleSubmit}
      onFocusCapture={ensureLoadRecaptcha}
      onMouseEnter={ensureLoadRecaptcha}
      className={clsx(
        "flex flex-col text-center w-full px-0 lg:px-5",
        variant === "dark"
          ? "bg-white/5 border border-white/20 backdrop-blur-[40px] rounded-2xl text-white"
          : "bg-white",
      )}
    >
      {variant === "dark" && (
        <h3 className="text-[clamp(1.125rem,0.6262rem+1.0393vw,2.25rem)] py-[15px] border-b border-solid border-white/20 font-medium text-left px-3 lg:px-8">
          Share Your Requirement
        </h3>
      )}

      <div className="w-full max-md:max-w-full px-4 pb-4 pt-4 xl:p-8 text-left">
        <div className="w-full max-md:max-w-full">
          {/* Row 1: Full Name + Email */}
          <div
            className={clsx(
              "flex 2xl:flex-row flex-col flex-wrap 2xl:gap-6 items-start w-full text-base max-md:max-w-full",
              variant === "dark" ? "text-white" : "text-zinc-600",
            )}
          >
            <div className="flex flex-1 flex-col w-[100%]">
              <FormInput
                label="Full Name"
                variant={variant}
                value={formData.fullName}
                onChange={handleInputChange("fullName")}
              />
              {errors.fullName && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.fullName}
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col w-[100%]">
              <FormInput
                label="Email Address"
                variant={variant}
                value={formData.email}
                onChange={handleInputChange("email")}
              />
              {errors.email && (
                <div className="text-red-500 text-xs mt-1">{errors.email}</div>
              )}
            </div>
          </div>

          {/* Row 2: Phone + Country */}
          <div
            className={clsx(
              "flex 2xl:flex-row flex-col flex-wrap 2xl:gap-6 items-start w-full text-base max-md:max-w-full",
              variant === "dark" ? "text-white" : "text-zinc-600",
            )}
          >
            <div className="flex flex-1 flex-col w-[100%]">
              {/*  Custom select */}
              <CustomSelect
                id="country-select"
                placeholder="Country"
                value={formData.country}
                options={countryList}
                variant={variant}
                onChange={(val) => handleChangeCountryAccCode(val)}
              />
              {errors.country && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.country}
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col w-[100%]">
              <PhoneInput
                variant={variant}
                selectedCountry={selectedCountry}
                countryListArray={countryListArray}
                setSelectedCountry={setSelectedCountry}
                phone={formData.phone}
                setFormData={setFormData}
              />

              {errors.phone && (
                <div className="text-red-500 text-xs mt-1">{errors.phone}</div>
              )}
            </div>
          </div>

          {/* Row 3: Budget + Teams ID */}
          <div
            className={clsx(
              "flex 2xl:flex-row flex-col flex-wrap 2xl:gap-6 items-start w-full text-base max-md:max-w-full",
              variant === "dark" ? "text-white" : "text-zinc-600",
            )}
          >
            <div className="flex flex-1 flex-col w-[100%]">
              {/*  Custom select */}
              <CustomSelect
                id="interested-in-select"
                placeholder="Interested In"
                value={formData.interestedIn}
                options={INTERESTED_IN_OPTIONS}
                variant={variant}
                searchable={false}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, interestedIn: val }))
                }
              />
              {errors.interestedIn && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.interestedIn}
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col w-[100%]">
              {/* Custom select  */}
              <CustomSelect
                id="budget-range-select"
                placeholder="Your Budget"
                value={formData.yourBudget}
                options={BUDGET_OPTIONS}
                variant={variant}
                searchable={false}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, yourBudget: val }))
                }
              />
              {errors.yourBudget && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.yourBudget}
                </div>
              )}
            </div>
          </div>

          {/* Row 3: Budget + Teams ID */}
          <div
            className={clsx(
              "flex 2xl:flex-row flex-col flex-wrap 2xl:gap-6 items-start w-full text-base max-md:max-w-full",
              variant === "dark" ? "text-white" : "text-zinc-600",
            )}
          >
            <div className="flex flex-1 flex-col w-[100%]">
              <FormInput
                label="MS Teams ID / WhatsApp No."
                className={clsx("min-h-[59px]")}
                variant={variant}
                value={formData.skypeWhatsapp}
                onChange={handleInputChange("skypeWhatsapp")}
              />
              {errors.skypeWhatsapp && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.skypeWhatsapp}
                </div>
              )}
            </div>
          </div>

          {/* Message */}
          <div className="w-full relative">
            <label
              className={clsx(
                "block text-base",
                variant === "dark" ? "text-white" : "text-zinc-600",
              )}
            >
              <textarea
                value={formData.message}
                onChange={handleInputChange("message")}
                onFocus={() => setIsMessageFocused(true)}
                onBlur={() => setIsMessageFocused(false)}
                className={clsx(
                  "w-full border-b border-solid bg-transparent outline-none resize-vertical transition-all",
                  variant === "dark"
                    ? "border-white/20 text-white placeholder-white/70"
                    : "border-b-[color:var(--Stroke-2,#E3E3E3)] text-zinc-600",
                  isMessageFocused || formData.message ? "pt-6 pb-2" : "py-4",
                  "min-h-[120px]",
                )}
                aria-label="Message"
              />
              <span
                className={clsx(
                  "absolute left-0 transition-all pointer-events-none",
                  isMessageFocused || formData.message
                    ? "text-sm top-1"
                    : "top-4",
                )}
              >
                Message
              </span>
            </label>
            {errors.message && (
              <div className="text-red-500 text-xs mt-1">{errors.message}</div>
            )}
          </div>

          {/* NDA + File Upload */}
          <div className="flex flex-wrap gap-3 my-7 2xl:gap-10 justify-between items-center w-full font-semibold">
            <div className="fit-content">
              <label
                className={clsx(
                  "flex gap-3 items-center text-sm cursor-pointer pb-3",
                  variant === "dark"
                    ? "text-white mx-auto text-center"
                    : "text-gray-950",
                )}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={isChecked}
                  onChange={(e) => {
                    const isCheckedValue = e.currentTarget.checked;
                    setIsChecked(isCheckedValue);
                    setFormData((prev) => ({
                      ...prev,
                      sendMeNda: isCheckedValue,
                    }));
                    setErrors((prev) => {
                      const copy = { ...prev };
                      delete copy.file;
                      return copy;
                    });
                  }}
                  aria-label="Send me NDA"
                />

                <span
                  className={clsx(
                    "w-5 h-5 rounded-full shrink-0 flex items-center justify-center border transition-all",
                    isChecked
                      ? "bg-[#222222] border-[#222222]"
                      : variant === "dark"
                        ? "border-white"
                        : "border-black",
                  )}
                  aria-hidden
                >
                  {isChecked ? (
                    <span className="block w-2.5 h-2.5 rounded-full bg-white" />
                  ) : null}
                </span>

                <span className="md:w-full w-[22ch]">
                  Yes, Send me a mutual NDA (Non-Disclosure Agreement)
                </span>
              </label>
              {errors.sendMeNda && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.sendMeNda}
                </div>
              )}
            </div>
            <div className="fit-content">
              {variant === "light" && (
                <FileUpload
                  variant={variant}
                  onUploadSuccess={(fileInfo: any) => {
                    setFormData((prev) => ({
                      ...prev,
                      file: fileInfo,
                    }));
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* reCAPTCHA */}
        <div className="flex flex-col items-start my-4">
          {recaptchaSiteKey ? (
            <>
              {loadRecaptcha && (
                <>
                  <Script
                    id="google-recaptcha"
                    src="https://www.google.com/recaptcha/api.js?render=explicit"
                    strategy="lazyOnload"
                    async
                  />
                  <ReCAPTCHA
                    sitekey={recaptchaSiteKey}
                    onChange={(token: string | null) => {
                      setRecaptchaToken(token || "");
                      setErrors((prev) => {
                        const copy = { ...prev };
                        delete copy.gRecaptchaResponse;
                        return copy;
                      });
                    }}
                  />
                </>
              )}
              {errors.gRecaptchaResponse && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.gRecaptchaResponse}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-yellow-600">
              reCAPTCHA not configured. Please set
              NEXT_PUBLIC_RECAPTCHA_SITE_KEY environment variable.
            </div>
          )}
        </div>

        {submitError && (
          <div className="text-red-500 text-sm mb-3">{submitError}</div>
        )}

        <button
          type="submit"
          aria-label="Send Your Inquiry"
          className={clsx(
            "group inline-flex items-center select-none cursor-pointer",
          )}
          style={{ width: "auto" }}
        >
          <span
            className={clsx(
              baseHeightClass,
              "rounded-full flex items-center justify-center font-semibold leading-[100%] whitespace-nowrap transition-colors",
              paddingClass,
              textSizeClass,
              "bg-white hover:bg-[#225BD4] hover:text-white text-[#00040b] border border-[#ECF0F6]",
            )}
          >
            Send Your Inquiry
          </span>

          <span
            className={clsx(
              iconWrapperClass,
              "shrink-0 rounded-full grid place-items-center transition-colors",
              "bg-[#225BD4]",
            )}
          >
            <Image
              src={Icons?.chevron}
              alt="Arrow Icon"
              width={iconSize}
              height={iconSize}
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="transition-transform duration-300 rotate-0 group-hover:rotate-[-90deg]"
            />
          </span>
        </button>

        <SecurityInfo variant={variant} />
      </div>
    </form>
  );
};

export default Form;
